
import axios from 'axios';

import {
  defineComponent,
  defineAsyncComponent,
  ref,
  reactive,
  onMounted,
  computed,
} from 'vue';

import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';

import PrimeVueToast from 'primevue/toast';

import { useToast } from 'primevue/usetoast';

import PrimeVueConfirmPopup from 'primevue/confirmpopup';

import { useConfirm } from 'primevue/useconfirm';

import PrimeVueTriStateCheckbox from 'primevue/tristatecheckbox';

import PrimeVueOverlayPanel from 'primevue/overlaypanel';

import type {
  EntriesPaginator,
  Project,
  Projects,
  Campaign,
  Campaigns,
  GenericObject,
} from '@/types';

import {
  TRAFFIC_TYPE_POP,
  TRAFFIC_TYPE_PUSH,
  TRAFFIC_TYPE_IN_PAGE_PUSH,
  TRAFFIC_TYPE_NATIVE,
  TRAFFIC_TYPE_BANNER,
  TRAFFIC_TYPES,
  TRAFFIC_TYPES_MAP,
  TRAFFIC_CATEGORIES,
  TRAFFIC_CATEGORIES_MAP,
  ENTRIES,
  CAMPAIGN_PAYMENT_TYPE_CPC,
  CAMPAIGN_PAYMENT_TYPE_CPA,
  CAMPAIGN_PAYMENT_TYPE_CPM,
  CAMPAIGN_PAYMENT_TYPES,
  CAMPAIGN_PAYMENT_TYPES_MAP,
  CAMPAIGN_APPROVAL_STATUSES,
  CAMPAIGN_APPROVAL_STATUSES_MAP,
  CAMPAIGN_STATUSES,
  CAMPAIGN_STATUSES_MAP,
  CREATIVE_IMAGE_FORMATS_MAP,
  ADSECURE_CHECK_STATUSES_MAP,
} from '@/libs/consts';

import useProject from '@/composable/useProject';

import useUser from '@/composable/useUser';

import useFolderActions from '@/composable/useFolderActions';

import useEntriesNext from '@/composable/useEntries@next';

import useUrlState from '@/composable/useUrlState';

import useStatisticsLink from '@/composable/useStatisticsLink';

export default defineComponent({
  components: {
    PrimeVueToast,
    PrimeVueConfirmPopup,
    PrimeVueOverlayPanel,
    PrimeVueTriStateCheckbox,
    CampaignApprovalStatus: defineAsyncComponent(() => import('@/components/CampaignApprovalStatus.vue')),
    CampaignStatus: defineAsyncComponent(() => import('@/components/CampaignStatus.vue')),
    CampaignArchivedAt: defineAsyncComponent(() => import('@/components/CampaignArchivedAt.vue')),
    UserColumn: defineAsyncComponent(() => import('@/components/UserColumn.vue')),
    Folders: defineAsyncComponent(() => import('@/components/Folders.vue')),
  },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    showProjectColumn: {
      type: Boolean,
      default: true,
    },
    showUserColumn: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const router = useRouter();

    const folderActions = useFolderActions();

    const i18n = useI18n();

    const toast = useToast();

    const confirm = useConfirm();

    const { activeProject: project }: { activeProject: Project } = useProject();

    const { activeUser: user } = useUser();

    const trafficTypes = [...TRAFFIC_TYPES].filter((option) => [TRAFFIC_TYPE_POP, TRAFFIC_TYPE_PUSH, TRAFFIC_TYPE_IN_PAGE_PUSH, TRAFFIC_TYPE_NATIVE, TRAFFIC_TYPE_BANNER].includes(option.value as number));

    const {
      entries: projects,
      entriesLoading: projectsLoading,
      fetchEntries: fetchProjects,
    } = useEntriesNext<Projects>('/api/project/getProjects', ENTRIES);

    const { encodeStatisticsFilters } = useStatisticsLink();

    const campaigns = reactive<Campaigns>(ENTRIES);

    const campaignOverlayPanel = ref();

    const campaignData = reactive<GenericObject>({
      id: null,
      bid: null,
      trafficType: null,
      loading: false,
    });

    const campaignFoldersOverlayPanel = ref();

    const campaignFoldersData = reactive<{
      id: number | null
      folderIds: number[]
      loading: boolean
    }>({
      id: null,
      folderIds: [],
      loading: false,
    });

    const campaignsFilters = useUrlState('filters', {
      project_id: null,
      user_id: null,
      name: null,
      traffic_type: null,
      traffic_category: null,
      payment_type: null,
      approval_status: null,
      status: null,
      archived: false,
      folder_id: null,
    });

    // Пикер папок пересоздаётся сменой ключа: своего события обновления у него нет.
    const foldersKeyCounter = ref(0);

    const usersParams = computed(() => ({
      include: 'project',
      'filter[project_id]': user.isSuperAdmin() ? campaignsFilters.project_id : undefined,
    }));

    const fetchCampaigns = async (campaignsPaginator?: EntriesPaginator): Promise<void> => {
      const response = await axios.post(
        '/api/campaign/getCampaignsPaginator',
        {
          ...(campaignsPaginator || {}),
          ...campaignsFilters,
          ...props.filters,
        },
      );

      Object.assign(campaigns, response.data);
    };

    const onProjectChange = (): void => {
      campaignsFilters.user_id = null;
    };

    const showArchived = (archived: boolean): void => {
      campaignsFilters.archived = archived;

      fetchCampaigns();
    };

    // Иначе фильтр остался бы указывать на удалённую папку, и список отдавал бы пусто.
    const onFolderDeleted = (folderId: number): void => {
      if (campaignsFilters.folder_id === folderId) {
        campaignsFilters.folder_id = null;
      }
    };

    const onFoldersChanged = (): void => {
      foldersKeyCounter.value += 1;

      fetchCampaigns();
    };

    const cloneCampaign = async (campaignId: number): Promise<void> => {
      try {
        const response = await axios.post('/api/campaign/cloneCampaign', {
          id: campaignId,
        });

        toast.add({
          severity: 'success',
          summary: i18n.t('success'),
          detail: i18n.t('success_clone_campaign'),
          life: 2000,
        });

        await router.push(`/campaigns/${response.data.id}`);
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: i18n.t('error'),
          detail: i18n.t('unknown_error'),
          life: 2000,
        });
      }
    };

    const confirmCloneCampaign = (event: PointerEvent, campaignId: number): void => {
      confirm.require({
        target: event.currentTarget as HTMLElement,
        message: 'Are you sure you want to clone this campaign?',
        icon: 'pi pi-question-circle',
        acceptIcon: 'pi pi-check',
        rejectIcon: 'pi pi-times',
        acceptLabel: 'Clone',
        rejectLabel: 'Cancel',
        rejectClass: 'p-button-outlined p-button-sm',
        acceptClass: 'p-button-sm',
        accept: () => {
          cloneCampaign(campaignId);
        },
      });
    };

    const toggleCampaignOverlayPanel = (event: PointerEvent, campaignId: number, campaignBid: number, campaignTrafficType: number): void => {
      campaignData.id = campaignId;

      campaignData.bid = campaignBid;

      campaignData.trafficType = campaignTrafficType;

      campaignOverlayPanel.value.toggle(event);
    };

    const updateBid = async (event: PointerEvent): Promise<void> => {
      campaignData.loading = true;

      try {
        await axios.post('/api/campaign/updateBid', {
          campaigns_ids: [campaignData.id],
          bid: campaignData.bid,
        });

        toast.add({
          severity: 'success',
          summary: i18n.t('success'),
          detail: i18n.t('success_bid_update'),
          life: 2000,
        });

        campaignOverlayPanel.value.toggle(event);

        const updatedCampaignIndex = campaigns.data.findIndex((campaign) => campaign.id === campaignData.id);

        campaigns.data[updatedCampaignIndex].bid = campaignData.bid;
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: i18n.t('error'),
          detail: i18n.t('unknown_error'),
          life: 2000,
        });
      }

      campaignData.loading = false;
    };

    const toggleCampaignFoldersOverlayPanel = (event: PointerEvent, campaign: Campaign): void => {
      campaignFoldersData.id = campaign.id ?? null;

      campaignFoldersData.folderIds = (campaign.folders || []).map((folder) => folder.id);

      campaignFoldersOverlayPanel.value.toggle(event);
    };

    const updateCampaignFolders = async (): Promise<void> => {
      // Идентификатор снимается заранее: панель могла быть переоткрыта на другой строке.
      const campaignId = campaignFoldersData.id;

      const index = campaigns.data.findIndex((campaign) => campaign.id === campaignId);

      // Строка могла уйти из выборки, пока панель была открыта.
      if (campaignId === null || index === -1) {
        campaignFoldersOverlayPanel.value.hide();

        return;
      }

      campaignFoldersData.loading = true;

      try {
        const response = await folderActions.setCampaignFolders(campaignId, campaignFoldersData.folderIds);

        // Ответ отдаёт папки в том же виде, что и список, поэтому строка обновляется
        // на месте — перечитывать весь список незачем.
        campaigns.data[index].folders = response.data.data;

        toast.add({
          severity: 'success',
          summary: i18n.t('success'),
          detail: i18n.t('success_store'),
          life: 2000,
        });

        campaignFoldersOverlayPanel.value.hide();
      } catch (error) {
        toast.add({
          severity: 'error',
          summary: i18n.t('error'),
          detail: axios.isAxiosError(error) && error.response?.status === 422
            ? Object.values(error.response.data.errors ?? {}).flat().join(' ')
            : i18n.t('unknown_error'),
          life: 4000,
        });
      }

      campaignFoldersData.loading = false;
    };

    onMounted(async (): Promise<void> => {
      if (user.isSuperAdmin()) {
        await fetchProjects();
      }
    });

    // TODO: Вынести в Vue 3 фильтры, чтобы использовать везде
    const truncateText = (text: string, length: number): string => {
      if (text.length < length) {
        return text;
      }

      return `${text.substring(1, length)}...`;
    };

    return {
      TRAFFIC_TYPE_POP,
      TRAFFIC_TYPES,
      TRAFFIC_TYPES_MAP,
      TRAFFIC_CATEGORIES,
      TRAFFIC_CATEGORIES_MAP,
      CAMPAIGN_PAYMENT_TYPE_CPC,
      CAMPAIGN_PAYMENT_TYPE_CPA,
      CAMPAIGN_PAYMENT_TYPE_CPM,
      CAMPAIGN_PAYMENT_TYPES,
      CAMPAIGN_PAYMENT_TYPES_MAP,
      CAMPAIGN_APPROVAL_STATUSES,
      CAMPAIGN_APPROVAL_STATUSES_MAP,
      CAMPAIGN_STATUSES,
      CAMPAIGN_STATUSES_MAP,
      CREATIVE_IMAGE_FORMATS_MAP,
      ADSECURE_CHECK_STATUSES_MAP,
      trafficTypes,
      project,
      user,
      projectsLoading,
      projects,
      encodeStatisticsFilters,
      campaigns,
      campaignData,
      campaignOverlayPanel,
      campaignsFilters,
      usersParams,
      fetchCampaigns,
      onProjectChange,
      showArchived,
      foldersKeyCounter,
      onFolderDeleted,
      onFoldersChanged,
      confirmCloneCampaign,
      toggleCampaignOverlayPanel,
      updateBid,
      campaignFoldersOverlayPanel,
      campaignFoldersData,
      toggleCampaignFoldersOverlayPanel,
      updateCampaignFolders,
      truncateText,
    };
  },
});
